import { NetworkStatusOptions } from '@strata/constants';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { FormContainer } from '@strata/react-form-container';
import { PhoneInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Formik } from 'formik';
import { useNavigate } from 'react-router';

import { AdditionalOpsContactsFields } from './AdditionalOpsContactsFields';
import { PayToInformationFields } from './PayToInformationFields';

import {
    ACCREDITATION_OPTIONS,
    ORG_SIZE_OPTIONS,
    PHARMACY_TYPE_OPTIONS,
    PSAO_OPTIONS,
    SWITCH_PROVIDER_OPTIONS,
} from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post } from 'services/Http';
import { packageSupplier, supplierInitialValues, supplierSchema } from 'services/NPISupport';

export const CreateSupplier = () => {
    const navigate = useNavigate();
    const goHome = () => navigate('/supplier-list');

    return (
        <Formik
            initialValues={supplierInitialValues}
            validationSchema={supplierSchema}
            onSubmit={async (values) => {
                try {
                    const body = packageSupplier(values);
                    await post('/reference/suppliers/', body);
                    goHome();
                } catch (err) {
                    handleError('Error creating supplier.', err);
                }
            }}
        >
            {(formik) => {
                const disableSave = formik.isSubmitting || !formik.isValid;
                return (
                    <FormContainer
                        formTitle="Add Supplier"
                        onFormSubmit={formik.handleSubmit}
                        isSubmitting={disableSave}
                        onSecondaryButtonClick={goHome}
                        formLeftContent={
                            <>
                                <div className="margin__bottom--medium font-bold--uppercase">
                                    Organization Information
                                </div>
                                <TextInputFormik label="Intake Supplier NPI" id="npi" required />
                                <TextInputFormik
                                    label="Billing Supplier NPI"
                                    id="billing_supplier_npi"
                                    required
                                />
                                <TextInputFormik
                                    label="Organization Name"
                                    id="organization_name"
                                    required
                                />
                                <SuggestSingleSelectFormik
                                    label="Organization Size"
                                    id="org_size"
                                    required
                                    values={ORG_SIZE_OPTIONS}
                                />
                                <SuggestSingleSelectFormik
                                    label="Pharmacy Type"
                                    id="pharmacy_type"
                                    values={PHARMACY_TYPE_OPTIONS}
                                />
                                <SuggestSingleSelectFormik
                                    id="psao"
                                    label="PSAO"
                                    values={PSAO_OPTIONS}
                                />
                                <PhoneInputFormik
                                    allowExtension
                                    label="Pharmacy Phone Number"
                                    id="telephone_number"
                                    required
                                />
                                <PhoneInputFormik label="Fax Number" id="fax_number" />
                                <TextInputFormik label="Address Line 1" id="address_1" required />
                                <TextInputFormik label="Address Line 2" id="address_2" />
                                <TextInputFormik label="City" id="city" required />
                                <TextInputFormik label="State" id="state" required />
                                <TextInputFormik label="Postal Code" id="postal_code" required />
                                <TextInputFormik label="Tax ID" id="tax_id" required />
                                <CheckboxListFormik
                                    name="accreditations"
                                    label="Accreditations"
                                    options={ACCREDITATION_OPTIONS}
                                />

                                <div className="margin__bottom--medium font-bold--uppercase">
                                    Operations Contact Information
                                </div>
                                <span className="font-regular--uppercase margin__bottom--medium">
                                    Primary Contact
                                </span>
                                <TextInputFormik
                                    label="Primary Ops Contact First Name"
                                    id="first_name"
                                    required
                                />
                                <TextInputFormik
                                    label="Primary Ops Contact Last Name"
                                    id="last_name"
                                    required
                                />
                                <TextInputFormik
                                    label="Primary Ops Contact Email"
                                    id="primary_contact_email"
                                />
                                <PhoneInputFormik
                                    allowExtension
                                    label="Primary Ops Contact Phone"
                                    id="primary_contact_phone"
                                />
                                <span className="font-regular--uppercase margin__bottom--medium">
                                    Secondary Contact
                                </span>
                                <TextInputFormik
                                    label="Secondary Ops Contact First Name"
                                    id="secondary_contact_first_name"
                                />
                                <TextInputFormik
                                    label="Secondary Ops Contact Last Name"
                                    id="secondary_contact_last_name"
                                />
                                <TextInputFormik
                                    label="Secondary Ops Contact Email"
                                    id="secondary_contact_email"
                                />
                                <PhoneInputFormik
                                    allowExtension
                                    label="Secondary Ops Contact Phone"
                                    id="secondary_contact_phone"
                                />
                                <AdditionalOpsContactsFields />

                                <PayToInformationFields />

                                <div className="margin__bottom--medium font-bold--uppercase">
                                    Other Contact Information
                                </div>
                                <TextInputFormik
                                    label="Business Contact Name"
                                    id="businessops_contact_name"
                                />
                                <TextInputFormik
                                    label="Business Contact Email"
                                    id="businessops_contact_email"
                                />
                                <PhoneInputFormik
                                    allowExtension
                                    label="Business Contact Phone"
                                    id="businessops_contact_phone"
                                />
                                <TextInputFormik
                                    label="Contracting Contact Name"
                                    id="contracting_contact_name"
                                />
                                <TextInputFormik
                                    label="Contracting Contact Email"
                                    id="contracting_contact_email"
                                />
                                <PhoneInputFormik
                                    allowExtension
                                    label="Contracting Contact Phone"
                                    id="contracting_contact_phone"
                                />
                                <TextInputFormik
                                    label="Legal Notice Email"
                                    id="legal_notice_email"
                                />
                                <TextInputFormik
                                    label="Breach Notice Email"
                                    id="breach_notice_email"
                                />

                                <div className="margin__bottom--medium font-bold--uppercase">
                                    Additional Information
                                </div>
                                <SuggestSingleSelectFormik
                                    id="network_status"
                                    label="Network Status"
                                    required
                                    values={NetworkStatusOptions}
                                    onChange={(newValue) =>
                                        formik.setFieldValue('network_status', newValue.value)
                                    }
                                    selectedItem={NetworkStatusOptions.find(
                                        ({ value }) => value === formik.values.network_status,
                                    )}
                                />
                                <TextInputFormik label="Business Hours" id="business_hours" />
                                <TextInputFormik label="Supplier Website" id="pharmacy_website" />
                                <SuggestSingleSelectFormik
                                    label="Switch Provider"
                                    id="switch_provider"
                                    values={SWITCH_PROVIDER_OPTIONS}
                                />
                                <TextInputFormik label="Pharmacy Software" id="pharmacy_software" />
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};
